import { SlideThumbnail } from "@/components/slides/list/SlideThumbnail";
import { useSlidesByLessonId } from "@/lib/hooks/slides/useSlidesByLessonId";
import { useTags } from "@/lib/hooks/tags/useTags";
import { CREATABLE_TEMPLATES, getTemplateLabel } from "@/lib/slides/slideTemplates";
import type { SlideTemplate } from "@ai-teach/types";
import { TagToggle } from "./TagPill";
import type { SubslideParent } from "./types";

const SELECT_CLASS =
  "rounded-lg border border-gray-200 bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 dark:border-gray-800";

export function SubslideParentPicker({
  lessonId,
  value,
  onChange,
}: {
  /** Absent where the rule is written away from any lesson, which only costs the preview below. */
  lessonId?: string;
  value: SubslideParent;
  onChange: (parent: SubslideParent) => void;
}) {
  const { tags } = useTags();
  const { lessonSlideItems, lessonSlidesLoading } = useSlidesByLessonId({
    lessonId: lessonId ?? "",
    skip: !lessonId,
  });

  // Mirrors what the generator itself matches on the server: same template, same tag, and a main
  // slide that can take subslides.
  const matches = lessonSlideItems.filter(
    (slide) =>
      slide.template === value.template &&
      (slide.tagIds ?? []).includes(value.tagId) &&
      !slide.isLinkedCopy
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Main slide template"
          value={value.template}
          onChange={(event) =>
            onChange({ ...value, template: event.target.value as SlideTemplate })
          }
          className={SELECT_CLASS}
        >
          {CREATABLE_TEMPLATES.map((template) => (
            <option key={template} value={template}>
              {getTemplateLabel(template)}
            </option>
          ))}
        </select>

        <input
          type="number"
          min={1}
          max={5}
          aria-label="Subslides per main slide"
          value={value.each}
          onChange={(event) =>
            onChange({ ...value, each: Math.min(5, Math.max(1, Number(event.target.value))) })
          }
          className={`w-20 ${SELECT_CLASS}`}
        />
        <span className="text-sm text-gray-500 dark:text-gray-400">per main slide</span>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {tags.map((tag) => (
          <TagToggle
            key={tag.id}
            tag={tag}
            selected={tag.id === value.tagId}
            onToggle={() => onChange({ ...value, tagId: tag.id === value.tagId ? "" : tag.id })}
          />
        ))}
      </div>

      {value.tagId === "" ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Pick the tag those main slides carry.
        </p>
      ) : !lessonId ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Every slide with this template and tag gets its own subslides, in whichever lesson this
          runs in.
        </p>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {lessonSlidesLoading && matches.length === 0
              ? "Looking for matching slides…"
              : `${matches.length} slide${matches.length === 1 ? "" : "s"} in this lesson match${
                  matches.length === 1 ? "es" : ""
                }, and ${matches.length === 1 ? "gets" : "each get"} ${value.each} subslide${
                  value.each === 1 ? "" : "s"
                }.`}
          </p>
          {matches.length === 0 ? (
            <p className="rounded-lg bg-warning-500/10 px-3 py-2 text-xs text-warning-700 dark:text-warning-500">
              Nothing to attach to: generating this section would fail until a slide here has this
              template and tag.
            </p>
          ) : (
            <ul className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              {matches.map((slide) => (
                <li key={slide.id} className="space-y-1">
                  <div className="pointer-events-none">
                    <SlideThumbnail slide={slide} handleCardClick={() => undefined} />
                  </div>
                  <p className="truncate text-[11px] text-gray-500">{slide.name}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
